import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import classNames from 'classnames';
import { Check, Copy } from 'lucide-react';
import { ComponentPropsWithoutRef, ReactElement, useState } from 'react';

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/Domain/components/Shared/Tooltip/Tooltip';
import { useCopiedFeedback } from '@/Domain/hooks/App/useCopiedFeedback.hook';
import { cn } from '@/Domain/utils/cn';
import { copyToClipboard } from '@/Domain/utils/copyToClipboard';
import { toDisplayValue } from '@/Domain/utils/toDisplayValue';

type Props = {
  label: string;
  'data-testid'?: string;
  tooltipContentProps?: ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>;
  tooltipValue?: string;
} & (
  | { value: unknown; ValueComponent?: never }
  | { value?: unknown; ValueComponent: ReactElement }
);

export const DetailsItem = ({
  label,
  value,
  ValueComponent,
  tooltipContentProps,
  tooltipValue,
  'data-testid': testId,
}: Props) => {
  const { copied, markCopied } = useCopiedFeedback();
  const [showCopyButton, setShowCopyButton] = useState(false);

  // Metadata values are arbitrary JSON; never render or copy `value` unstringified.
  const displayValue = toDisplayValue(value);

  const handleCopy = () => {
    if (copied) return;
    copyToClipboard(displayValue, markCopied);
  };

  const hasValue = displayValue || ValueComponent;

  return (
    <section
      className="grid grid-cols-2 items-center leading-5 tracking-wide gap-x-0"
      data-testid={testId}
    >
      <p className="text-sm text-muted-foreground">{label}</p>
      <TooltipProvider>
        <Tooltip open={hasValue && !showCopyButton ? undefined : false}>
          <TooltipTrigger asChild>
            <div
              className="relative w-full overflow-visible"
              data-testid="details-item-value"
              onMouseEnter={() => setShowCopyButton(true)}
              onMouseLeave={() => setShowCopyButton(false)}
            >
              {ValueComponent ? (
                ValueComponent
              ) : (
                <p
                  className={classNames('truncate text-sm text-foreground', {
                    'text-muted-foreground': !displayValue,
                  })}
                >
                  {displayValue || '-'}
                </p>
              )}
              {hasValue && (
                <div
                  data-testid="details-item-copy-overlay"
                  className={cn(
                    // Only the button takes pointer events: the overlay spans the whole value, and swallowing clicks would make a link ValueComponent unclickable.
                    'pointer-events-none absolute inset-0 z-10 flex items-center backdrop-blur-[0.5px] transition-opacity duration-200',
                    showCopyButton ? 'opacity-100' : 'opacity-0',
                  )}
                >
                  <button
                    type="button"
                    onClick={handleCopy}
                    aria-label={copied ? 'Copied' : 'Copy'}
                    className={cn(
                      'flex items-center gap-1 rounded-md border border-border bg-card px-2 py-0.5 text-[10px] text-muted-foreground hover:bg-accent focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
                      showCopyButton && !copied ? 'pointer-events-auto' : 'pointer-events-none',
                    )}
                  >
                    {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                    <span>{copied ? 'Copied' : 'Copy'}</span>
                  </button>
                </div>
              )}
            </div>
          </TooltipTrigger>
          <TooltipContent {...tooltipContentProps}>{tooltipValue ?? displayValue}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </section>
  );
};
